import type { Trace } from '@/features/traceability/type/Tool';
import type { MetricKey } from '../type/dynamicChart';
import type { AggregationKey } from '../registry/aggregations';
import { aggregationRegistry } from '../registry/aggregations';
import { metricRegistry } from '../registry/metrics';

/**
 * Compute the Y value for a group of traces using the specified metric and aggregation.
 *
 * - Looks up the metric definition from metricRegistry.
 * - Resolves the aggregation key: uses aggregationKey if it exists in aggregationRegistry,
 *   otherwise falls back to metricDef.defaultAggregation.
 * - Extracts numeric values from traces via metricDef.extract.
 * - Applies the aggregation function to the numeric values.
 * - Returns 0 for empty input, unknown metric, or missing aggregation.
 */
export function computeYFromTraces(
  traces: Trace[],
  metricKey: MetricKey,
  aggregationKey: AggregationKey | undefined
): number {
  const metricDef = metricRegistry.find((m) => m.key === metricKey);
  if (!metricDef) return 0;

  const resolvedKey =
    aggregationKey && aggregationRegistry.some((a) => a.key === aggregationKey)
      ? aggregationKey
      : metricDef.defaultAggregation;

  const aggDef =
    aggregationRegistry.find((a) => a.key === resolvedKey) ??
    aggregationRegistry.find((a) => a.key === metricDef.defaultAggregation);

  if (!aggDef) return 0;

  const rawValues = traces
    .map((t) => metricDef.extract(t))
    .filter((v): v is number => typeof v === 'number');

  return rawValues.length === 0 ? 0 : aggDef.fn(rawValues);
}
